// src/components/PerformanceStats.js - VERSION CORRIGÉE AVEC DONNÉES RÉELLES

import React from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faCoins, faMoneyBillWave, faCube, faMicrochip, faInfinity, faTachometerAlt } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next'; // Pour la traduction
import PortfolioGrowthChart from './PortfolioGrowthChart'; // <-- NOUVEL IMPORT DU GRAPHIQUE

// --- FONCTIONS UTILITAIRES ---

// Formatage XOF
const formatAmount = (amount) => {
    const validAmount = amount || 0; 
    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF', 
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(validAmount);
};

// Couleurs et Icônes des plans pour la cohérence visuelle
const getPlanVisuals = (planName) => {
    if (planName.toUpperCase().includes('OMEGA')) {
        return { color: '#003366', icon: faCube }; // Bleu foncé
    }
    if (planName.toUpperCase().includes('G3N')) {
        return { color: '#ff8c00', icon: faMicrochip }; // Orange
    }
    if (planName.toUpperCase().includes('INFINITY')) {
        return { color: '#40E0D0', icon: faInfinity }; // Turquoise
    }
    return { color: '#6c757d', icon: faMicrochip }; // Gris par défaut
};

// --- COMPOSANT PRINCIPAL ---

const PerformanceStats = ({ performanceStats }) => {
    const { t } = useTranslation();
    
    // ✅ EXTRACTION DES NOUVELLES DONNÉES RÉELLES
    const globalReturn = performanceStats?.global_return || 0;
    const globalReturnFormatted = performanceStats?.global_return_formatted || formatAmount(0);
    const netGainRealized = performanceStats?.net_gain_realized || 0;
    const netGainRealizedFormatted = performanceStats?.net_gain_realized_formatted || formatAmount(0);
    const successRate = performanceStats?.success_rate || '0.0%';
    const gainsByPlan = performanceStats?.gains_by_plan || [];
    const portfolioGrowth = performanceStats?.portfolio_growth || [];
    
    // ✅ CONSERVATION POUR COMPATIBILITÉ (anciennes données)
    const totalGainByPlan = performanceStats?.totalGainByPlan || gainsByPlan;
    const MOCK_TOTAL_INVESTED = performanceStats?.MOCK_TOTAL_INVESTED || 0;
    const MOCK_Taux_Succes = performanceStats?.MOCK_Taux_Succes || parseFloat(successRate) || 0;
    
    // Vérification si des données sont disponibles
    const hasData = globalReturn > 0 || netGainRealized > 0 || gainsByPlan.length > 0;
    
    if (!hasData) {
        return (
            <div className="p-4">
                <h4 className="mb-3 fw-bold text-dark">{t('performance_dashboard_title')}</h4>
                <div className="alert alert-info" role="alert">
                    {t('no_performance_data_available')}
                </div>
            </div>
        );
    }

    // ✅ CALCULS AVEC LES DONNÉES RÉELLES
    const totalGlobalGain = globalReturn || totalGainByPlan.reduce((acc, stat) => acc + (stat.gain || 0), 0);
    
    // Pourcentage de rendement global (calculé si pas disponible)
    const globalReturnPercentage = performanceStats?.global_return_percentage || 
        (MOCK_TOTAL_INVESTED > 0 ? (totalGlobalGain / MOCK_TOTAL_INVESTED) * 100 : 0);
    
    // Données pour le graphique
    const chartData = portfolioGrowth.length > 0 ? portfolioGrowth : performanceStats?.balanceHistory;
    
    // --- Rendu du tableau de bord stylisé ---
    return (
        <div className="performance-stats-container p-4">
            <h4 className="mb-4 fw-bolder text-dark">{t('ai_performance_dashboard_title')}</h4>
            
            {/* 1. VUE D'ENSEMBLE RAPIDE (The Scorecard) */}
            <Row className="g-3 mb-4">
                
                {/* 1.1 Performance Globale (Rendement) */}
                <Col xs={12}> {/* Prend toute la largeur */}
                    <Card className="h-100 shadow-sm border-0 p-3" style={{ borderLeft: '5px solid var(--bs-success)' }}>
                        <Card.Body className="p-0"> {/* Réduit le padding interne de la Card.Body */}
                            <p className="text-uppercase text-muted small mb-1">{t('global_return')}</p>
                            <h3 className="fw-bolder mb-0 text-success d-flex align-items-center justify-content-between">
                                {globalReturnFormatted}
                                <FontAwesomeIcon icon={faChartLine} className="ms-2" size="lg" style={{ opacity: 0.5 }} />
                            </h3>
                            {/* Affichage du pourcentage si disponible */}
                            {globalReturnPercentage > 0 && (
                                <p className="text-muted small mb-0 mt-1">
                                    {globalReturnPercentage.toFixed(2)}% de rendement
                                </p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* 1.2 Gain Net Total */}
                <Col xs={6}>
                    <Card className="h-100 shadow-sm border-0 p-3" style={{ borderLeft: '5px solid var(--bs-primary)' }}>
                        <Card.Body className="p-0">
                            <p className="text-uppercase text-muted small mb-1">{t('net_gain_realized')}</p>
                            <h5 className="fw-bolder mb-0 text-primary">
                                {netGainRealizedFormatted}
                            </h5>
                        </Card.Body>
                    </Card>
                </Col>

                {/* 1.3 Taux de Succès */}
                 <Col xs={6}>
                    <Card className="h-100 shadow-sm border-0 p-3" style={{ borderLeft: '5px solid var(--bs-purple)' }}>
                        <Card.Body className="p-0">
                            <p className="text-uppercase text-muted small mb-1">{t('success_rate')}</p>
                            <h5 className="fw-bolder mb-0 text-purple d-flex align-items-center justify-content-between">
                                {successRate}
                                <FontAwesomeIcon icon={faTachometerAlt} className="ms-2" style={{ opacity: 0.5 }} />
                            </h5>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* --- 2. DÉTAIL PAR PLAN (Cartes) --- */}
            <h5 className="mb-3 mt-4 fw-bold text-dark border-bottom pb-2">{t('gains_by_plan')}</h5>
            <Row className="g-3">
                {gainsByPlan.map((stat, index) => {
                    const visuals = getPlanVisuals(stat.plan);
                    const gainAmount = stat.gain || 0;
                    const gainFormatted = stat.gainFormatted || formatAmount(gainAmount);
                    const gainColorClass = gainAmount > 0 ? 'text-success' : (gainAmount < 0 ? 'text-danger' : 'text-secondary');

                    return (
                        <Col xs={12} key={index}>
                            <Card className="h-100 shadow-sm" style={{ 
                                border: `1px solid ${visuals.color}60`, 
                                borderLeft: `5px solid ${visuals.color}`,
                                backgroundColor: '#ffffff' 
                            }}>
                                <Card.Body className="py-2 px-3 d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon icon={visuals.icon} style={{ color: visuals.color }} className="me-3 fs-5" /> {/* Augmente la taille de l'icône */}
                                        <div className="fw-bolder fs-6" style={{ color: visuals.color }}> {/* Ajuste la taille de police */}
                                            {stat.plan}
                                        </div>
                                    </div>
                                    
                                    <h6 className={`fw-bolder mb-0 ${gainColorClass}`}>
                                        {gainFormatted}
                                    </h6>
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            {/* 3. ANALYSE TEMPORELLE (GRAPHIQUE INTERACTIF) */}
            <div className="mt-4 pt-2 border-top">
                <h5 className="mb-3 fw-bold text-dark">{t('portfolio_growth_history')}</h5>
                <Card className="shadow-sm p-3">
                    {/* Le composant de graphique ici */}
                    <PortfolioGrowthChart data={chartData} />
                </Card>
            </div>
        </div>
    );
};

export default PerformanceStats;